import styled, { css } from 'styled-components';
import tinycolor from 'tinycolor2';
import Button from 'src/components/core/button';
import { KIOSK_PRO_MEDIA, kioskTouchHints } from '../kiosk-pro-shared/kiosk-pro-shared.styles';

/**
 * Lock screen wrapper — matches Figma "Wrapper" container.
 * Full-height flex column that holds the logo section and content section.
 *
 * Normal viewports: grid layout matching develop's PageContainer behavior
 * (logo left, content right in landscape; stacked centered in portrait).
 *
 * Small viewports: single-column flex layout with logo at top, content centered below.
 */
export const LockScreenWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  min-height: 100dvh;
  justify-items: center;
  align-items: stretch;
  text-align: center;
  padding: 72px;
  box-sizing: border-box;

  @media (orientation: landscape) {
    grid-template-columns: auto 1fr;
    grid-template-rows: 1fr;
    align-items: center;
    gap: 2rem;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding: 56px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100svh;
    max-height: 100svh;
    padding: 48px;
    box-sizing: border-box;
    overflow: hidden;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: 100svh;
    padding: 48px;
    box-sizing: border-box;
  }
`;

/** Logo section — shrinks to content, doesn't grow. Zeroes out KioskProLogo internal padding. */
export const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px;

  /* Zero out KioskProLogo's internal padding — LogoSection controls spacing */
  [data-testid='kiosk-pro-logo-container'] {
    padding: 0;
  }

  img {
    max-height: 220px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding: 28px;

    img {
      max-height: 156px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscape} {
    padding: 24px;
    flex-shrink: 0;

    img {
      max-height: 112px;
    }
  }
`;

/**
 * Content section — fills remaining space and centers its children vertically.
 * Matches Figma "Section" (the flex: 1 content area).
 */
export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 72px;
  width: 100%;

  a,
  button {
    ${kioskTouchHints}
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    gap: 56px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    flex: 1;
    gap: 48px;
    min-height: 0;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscape} {
    flex: 1;
    gap: 32px;
  }
`;

export const LockScreenTitle = styled.h1`
  margin: 0;
  font-size: 220px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  line-height: 0.8;

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth}, ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 156px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 112px;
  }
`;

export const LockScreenTitleContainer = styled.div`
  /**
   * Fixed widths to force text wrapping for "ORDER & PAY HERE" / "ORDER HERE"
   * to match Figma. Calibrated for the current uppercase font at each breakpoint's
   * font-size (220px / 156px / 112px). Recalibrate if title text or font changes.
   */
  max-width: 75%;

  @media (orientation: portrait) {
    max-width: 100%;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    max-width: 710px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    max-width: 555px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    max-width: 515px;
  }
`;

/** CTA group — contains the subtitle and buttons. Matches Figma "CTAs" container. */
export const CTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 36px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    gap: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    gap: 24px;
  }
`;

export const MenuTypeTitle = styled.p`
  font-size: 36px;
  margin: 0;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscape} {
    font-size: 24px;
    opacity: 0.75;
  }
`;

const baseButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  width: 556px;
  height: 168px;
  padding: 36px 112px;
  line-height: 1;
  font-size: 56px;
  border-radius: 800px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    width: 397px;
    height: 132px;
    font-size: 40px;
    padding: 28px 80px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly}, ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    width: 342px;
    height: 116px;
    font-size: 36px;
    padding: 24px 64px;
    gap: 14px;
  }
`;

export const PrimaryButton = styled(Button)`
  ${baseButtonStyles}
`;

export const SecondaryButton = styled(Button)`
  ${baseButtonStyles}
  background-color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};

  &:disabled {
    color: transparent;
  }
`;

export const SubText = styled.p`
  color: inherit;
  font-size: 24px;
  font-weight: 500;
  opacity: 0.75;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth}, ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 18px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: 16px;
  }
`;

export const AndSign = styled.span`
  color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 30).toString()};
`;

/** Identifies which kiosk a tablet is provisioned as. Own high-contrast chip background so it
 * stays legible against any store-configured brand background. */
export const KioskIdentityLabel = styled.div`
  background-color: var(--dcm-kiosk-identity-bg, rgba(0, 0, 0, 0.65));
  border-radius: var(--dcm-kiosk-identity-radius, 999px);
  color: var(--dcm-kiosk-identity-color, #fff);
  font-size: var(--dcm-kiosk-identity-font-size, 18px);
  font-weight: var(--dcm-kiosk-identity-font-weight, 600);
  padding: var(--dcm-kiosk-identity-padding, 8px 16px);
  position: fixed;
  right: var(--dcm-kiosk-identity-right, 16px);
  top: var(--dcm-kiosk-identity-top, 16px);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait}, ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    font-size: var(--dcm-kiosk-identity-font-size-small, 16px);
  }
`;

/* Invisible stand-in shown in the chip's place once hidden, so the staff gesture still has
   something to tap. No background/border/cursor so it stays undetectable. */
export const KioskIdentityTapRegion = styled.div`
  position: fixed;
  right: var(--dcm-kiosk-identity-right, 16px);
  top: var(--dcm-kiosk-identity-top, 16px);
  width: var(--dcm-kiosk-identity-tap-region-size, 64px);
  height: var(--dcm-kiosk-identity-tap-region-size, 64px);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;
